'use client'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { analytics, trackEvent } from '@/lib/analytics'
import { Button } from '@/components/ui/button'
import { InlineScanCta } from '@/components/blog/inline-scan-cta'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'
import { IDENTIFY_CTA, identifyCtaProps } from '@/lib/identify-cta'

/**
 * The two CTA slots on the head-term landing page, split into their own client
 * module so page.tsx stays a server component and the page stays statically
 * exported. The client boundary is these two elements, not the page.
 *
 * Why they live together: they are one measurement. The hero opens the photo
 * picker in place (the same InlineScanCta the blog posts use), the closing card
 * still links to /#analysis-card, and both report `cta_surface_shown` so the
 * pair is an exposure-matched read on inline upload vs a second hop. Splitting
 * them across files invites one of them to lose its impression later.
 *
 * `cta_variant` is the only thing separating the two arms. Do not fold it into
 * cta_location; the locations are per-slot and already carry position.
 */

/** Deliverable chips, matching the identify V2 arm on the blog inline CTA. */
const LANDING_PREVIEW_CHIPS = ['Grass type with confidence', 'Care plan for your ZIP']

/**
 * Copy is the IDENTIFY_COPY_MAP 'what kind of grass' entry from lib/blog-intent,
 * which is this page's head term verbatim, so the landing page and the blog post
 * chasing the same phrase read identically. Copied rather than imported: that
 * module pulls ARTICLE_PRODUCT_PICKS and the widget-placement helpers, none of
 * which belong in this page's client bundle. No em-dashes (house style).
 */
const HERO_COPY = {
  heading: 'Find out what grass you have in seconds',
  tagline:
    'Snap a photo of your lawn right here and get your answer with a confidence score',
} as const

/** Hero slot: the in-place picker. */
export function LandingHeroScanCta() {
  return (
    <div className="mt-6">
      <InlineScanCta
        ctaLocation={IDENTIFY_CTA.location.grassIdLandingHero}
        heading={HERO_COPY.heading}
        tagline={HERO_COPY.tagline}
        previewChips={LANDING_PREVIEW_CHIPS}
        analyticsProps={{ cta_variant: 'inline_scan', slot: 'hero' }}
      />
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          href="/blog/how-to-identify-your-grass-type-by-look-feel"
          className="text-sm font-semibold text-green-700 hover:text-green-800 underline underline-offset-4 decoration-2"
        >
          Prefer to identify it by hand?
        </Link>
      </div>
    </div>
  )
}

/**
 * Closing slot: still a link into the homepage flow, now with a denominator.
 * This is the control arm. If it is ever converted to a picker too, the page
 * loses the comparison it exists to produce.
 */
export function LandingFinalLinkCta() {
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    IDENTIFY_CTA.location.grassIdLandingFinal,
    { surface: 'grass_id_landing', cta_variant: 'link', slot: 'final' },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <Button
      asChild
      size="lg"
      variant="secondary"
      className="mt-6 bg-white text-green-700 hover:bg-green-50 font-bold px-7 py-4 text-base shadow-lg min-h-[48px] group"
    >
      <Link
        ref={impressionRef}
        href={IDENTIFY_CTA.href}
        onClick={() =>
          trackEvent('cta_clicked', {
            ...identifyCtaProps(IDENTIFY_CTA.location.grassIdLandingFinal),
            cta_variant: 'link',
            slot: 'final',
          })
        }
      >
        Identify my grass now
        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </Link>
    </Button>
  )
}
